import { useState, useEffect, useRef } from "react";
import { getRailsClient } from "sj-ui-toolkit/src/client";
import { ActivityData } from "@/api/activities";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RawAdminUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

interface RawAdminsResponse {
  admins: RawAdminUser[];
}

export interface AssigneeOption {
  label: string;
  value: string;
  firstName: string;
  lastName: string;
}

interface Assignee {
  id: number;
  firstName: string;
  lastName: string;
}

interface AssigneesResponse {
  assignees: Assignee[];
}

interface DojoActivityAssigneeResponse {
  dojoActivityAssignee?: { id: number };
}

// Maps activityId → array of assigned admin value strings (id.toString())
type ActivityAssignments = Record<number, string[]>;
// Maps activityId → adminValue → assigneeRecordId
type AssigneeRecordMap = Record<number, Record<string, number>>;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useActivityAssignments = (activities: ActivityData[]) => {
  const [admins, setAdmins] = useState<AssigneeOption[]>([]);
  const [activityAssignments, setActivityAssignments] = useState<ActivityAssignments>({});
  const [assigneeRecordMap, setAssigneeRecordMap] = useState<AssigneeRecordMap>({});
  const adminsLoadedRef = useRef(false);
  const loadedActivityIdsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!adminsLoadedRef.current) {
      adminsLoadedRef.current = true;
      void loadAdmins();
    }
  }, []); // empty deps — intentional: load once on mount

  useEffect(() => {
    if (admins.length > 0 && activities.length > 0) {
      const newActivities = activities.filter(a => !loadedActivityIdsRef.current.has(a.id));
      if (newActivities.length > 0) {
        void loadAllAssignees(newActivities);
      }
    }
  }, [admins, activities]);

  const loadAdmins = async () => {
    try {
      const API = `/api/internal_api/v1/admin/admins`;
      const { data } = await getRailsClient().get<RawAdminsResponse>(API);
      const adminsList = data.admins ?? [];
      const mapped: AssigneeOption[] = adminsList
        .map(admin => ({
          label: `${admin.firstName} ${admin.lastName}`.trim() || admin.email,
          value: admin.id.toString(),
          firstName: admin.firstName,
          lastName: admin.lastName,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setAdmins(mapped);
    } catch (err) {
      console.error("Failed to load admins:", err);
    }
  };

  const loadAllAssignees = async (activitiesToLoad: ActivityData[]) => {
    const API = `/api/internal_api/v1/admin/activities/dojo_activity_assignees`;
    try {
      const assigneePromises = activitiesToLoad.map(activity =>
        getRailsClient()
          .get<AssigneesResponse>(API, { params: { dojo_activity_id: activity.id } })
          .then(res => ({ activityId: activity.id, assignees: res.data.assignees ?? [] }))
          .catch(err => {
            console.error(`Failed to load assignees for activity ${activity.id}:`, err);
            return { activityId: activity.id, assignees: [] as Assignee[] };
          })
      );

      const results = await Promise.all(assigneePromises);

      const newAssignments: ActivityAssignments = {};
      const newRecordMap: AssigneeRecordMap = {};

      results.forEach(({ activityId, assignees }) => {
        const adminIds: string[] = [];
        const recordMap: Record<string, number> = {};

        assignees.forEach(assignee => {
          const matchingAdmin = admins.find(
            admin => admin.firstName === assignee.firstName && admin.lastName === assignee.lastName
          );
          if (matchingAdmin) {
            adminIds.push(matchingAdmin.value);
            recordMap[matchingAdmin.value] = assignee.id;
          }
        });

        newAssignments[activityId] = adminIds;
        newRecordMap[activityId] = recordMap;
      });

      setActivityAssignments(prev => ({ ...prev, ...newAssignments }));
      setAssigneeRecordMap(prev => ({ ...prev, ...newRecordMap }));
      results.forEach(({ activityId }) => loadedActivityIdsRef.current.add(activityId));
    } catch (err) {
      console.error("Failed to load assignees:", err);
    }
  };

  const handleAssignmentChange = async (activityId: number, newAdminIds: string[]) => {
    const API = `/api/internal_api/v1/admin/activities/dojo_activity_assignees`;
    const previousAdminIds = activityAssignments[activityId] ?? [];
    const addedAdmins = newAdminIds.filter(id => !previousAdminIds.includes(id));
    const removedAdmins = previousAdminIds.filter(id => !newAdminIds.includes(id));

    // Optimistically update UI
    setActivityAssignments(prev => ({ ...prev, [activityId]: newAdminIds }));

    try {
      for (const adminId of addedAdmins) {
        const { data } = await getRailsClient().post<DojoActivityAssigneeResponse>(API, {
          dojo_activity_assignee: {
            dojo_activity_id: activityId,
            user_id: parseInt(adminId, 10),
          },
        });
        const newAssigneeId = data.dojoActivityAssignee?.id;
        if (newAssigneeId !== undefined) {
          setAssigneeRecordMap(prev => ({
            ...prev,
            [activityId]: { ...(prev[activityId] ?? {}), [adminId]: newAssigneeId },
          }));
        }
      }

      for (const adminId of removedAdmins) {
        const assigneeId = assigneeRecordMap[activityId]?.[adminId];
        if (assigneeId !== undefined) {
          await getRailsClient().delete(`${API}/${assigneeId}`);
          setAssigneeRecordMap(prev => {
            const updated = { ...prev };
            if (updated[activityId]) {
              const activityMap = { ...updated[activityId] };
              delete activityMap[adminId];
              updated[activityId] = activityMap;
            }
            return updated;
          });
        }
      }
    } catch (err) {
      console.error("Failed to update admin assignments:", err);
      // Revert on error
      setActivityAssignments(prev => ({ ...prev, [activityId]: previousAdminIds }));
    }
  };

  return { admins, activityAssignments, handleAssignmentChange };
};
